import { ReportStep } from '../hooks/report/types';
import i18n from '../i18n';

/**
 * Format processing time in seconds to a human-readable string
 * @param seconds Processing time in seconds
 * @returns Formatted time string
 */
export const formatProcessingTime = (seconds: number): string => {
  if (seconds < 60) {
    return i18n.t('common:time.sec', { val: Math.round(seconds) });
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return i18n.t('common:time.min_sec', { min: minutes, sec: remainingSeconds });
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return i18n.t('common:time.hr_min_sec', { hr: hours, min: minutes, sec: remainingSeconds });
  }
};

/**
 * Format reports from analytics data
 * @param reports Reports from analytics data
 * @returns Formatted reports
 */
export const formatReportsFromAnalytics = (reports: any[]): ReportStep[] => {
  return reports.map((report: any, idx: number) => ({
    name: report.name,
    status: report.status || 'pending',
    index: report.index !== undefined ? report.index : idx, // Use original index from backend
    sheets: report.sheets || [],
    processing_steps: report.processing_steps || [],
    query_suggestion: report.query_suggestion || '',
    error: report.error || null,
    deleted: report.deleted || false,
    processing_start_time: report.processing_start_time || undefined,
    processing_end_time: report.processing_end_time || undefined,
    processing_duration_seconds: report.processing_duration_seconds || undefined
  }));
};

/**
 * Get non-deleted report indices
 * @param reports Reports array
 * @returns Array of non-deleted report indices
 */
export const getNonDeletedIndices = (reports: ReportStep[]): number[] => {
  return reports
    .filter((report: ReportStep) => !report.deleted)
    .map((report: ReportStep) => report.index);
};

/**
 * Filter reports by status
 * @param reports Reports array
 * @param status Status to filter by
 * @param excludeDeleted Whether to exclude deleted reports
 * @returns Filtered reports
 */
export const filterReportsByStatus = (
  reports: ReportStep[], 
  status: 'pending' | 'processing' | 'success' | 'error',
  excludeDeleted: boolean = true
): ReportStep[] => {
  return reports.filter(
    (report: ReportStep) => 
      report.status === status && 
      (excludeDeleted ? !report.deleted : true)
  );
};

/**
 * Get status text for display
 * @param status Report status
 * @returns Human-readable status text
 */
export const getStatusText = (status: string): string => {
  const key = ({ success: 'success', processing: 'processing', error: 'error', pending: 'pending' } as Record<string, string>)[status] || 'unknown';
  return i18n.t(`common:status.${key}`);
};

/**
 * Update report status in reports array
 * @param reports Reports array
 * @param reportIndex Report index to update
 * @param status New status
 * @param error Optional error message
 * @returns Updated reports array
 */
export const updateReportStatus = (
  reports: ReportStep[],
  reportIndex: number,
  status: 'pending' | 'processing' | 'success' | 'error',
  error: string | null = null
): ReportStep[] => {
  return reports.map((report: ReportStep) => 
    report.index === reportIndex 
      ? { ...report, status, error }
      : report
  );
};

/**
 * Find next pending report
 * @param reports Reports array
 * @returns Next pending report or undefined if none found
 */
export const findNextPendingReport = (reports: ReportStep[]): ReportStep | undefined => {
  return reports.find(
    report => report.status === 'pending' && !report.deleted
  );
};
